// Location utilities for delivery radius checking
const MAX_DELIVERY_RADIUS_KM = 10; // Maximum delivery radius
const ZONE1_RADIUS_KM = 4; // Inner zone (cheaper delivery)
const ZONE2_RADIUS_KM = 10; // Outer zone (more expensive delivery)

// Delivery pricing zones
export const DELIVERY_ZONES = {
  ZONE1: { maxKm: ZONE1_RADIUS_KM, fee: 10, name: 'Zone 1' }, // 0-4km = ₹10
  ZONE2: { maxKm: ZONE2_RADIUS_KM, fee: 20, name: 'Zone 2' }, // 4-10km = ₹20
  OUT_OF_RANGE: { maxKm: null, fee: null, name: 'Out of Range' } // >10km = No delivery
};

export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
      },
      (error) => {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            reject(new Error('Location access denied by user'));
            break;
          case error.POSITION_UNAVAILABLE:
            reject(new Error('Location information is unavailable'));
            break;
          case error.TIMEOUT:
            reject(new Error('Location request timed out'));
            break;
          default:
            reject(new Error('An unknown error occurred'));
            break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  });
};

// Calculate distance between two points using Haversine formula
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  
  return distance;
};

const toRadians = (degrees) => {
  return degrees * (Math.PI / 180);
};

// Get delivery zone and pricing based on distance
export const getDeliveryZone = (distance) => {
  const roundedDistance = Math.round(distance * 100) / 100; // Round to 2 decimal places
  
  if (roundedDistance <= ZONE1_RADIUS_KM) {
    return {
      zone: DELIVERY_ZONES.ZONE1,
      distance: roundedDistance,
      deliveryAvailable: true,
      deliveryFee: DELIVERY_ZONES.ZONE1.fee
    };
  } else if (roundedDistance <= ZONE2_RADIUS_KM) {
    return {
      zone: DELIVERY_ZONES.ZONE2,
      distance: roundedDistance,
      deliveryAvailable: true,
      deliveryFee: DELIVERY_ZONES.ZONE2.fee
    };
  } else {
    return {
      zone: DELIVERY_ZONES.OUT_OF_RANGE,
      distance: roundedDistance,
      deliveryAvailable: false,
      deliveryFee: null
    };
  }
};

// Check if user is within delivery radius with zone-based pricing
export const isWithinDeliveryRadius = (restaurantLat, restaurantLon, userLat, userLon) => {
  const distance = calculateDistance(restaurantLat, restaurantLon, userLat, userLon);
  const deliveryInfo = getDeliveryZone(distance);
  
  return {
    withinRadius: deliveryInfo.deliveryAvailable,
    distance: deliveryInfo.distance,
    maxRadius: MAX_DELIVERY_RADIUS_KM,
    deliveryFee: deliveryInfo.deliveryFee,
    zone: deliveryInfo.zone,
    deliveryAvailable: deliveryInfo.deliveryAvailable
  };
};